import { useState } from "react";
import { ApiError } from "../../api/client";
import type { Authorization, Order } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { Dialog } from "../../ui/Dialog";
import { DAILY_LIMIT, deleteOne, MIN_REASON, sortForDelete } from "./delete-orders";

interface Props {
  /** The orders ticked in the list. */
  orders: Order[];
  /** The owner's code, given just before. */
  authorization: Authorization;
  /** The special admin code from Tafo, entered behind the small ⚙. */
  adminCode: string;
  /** Tafo changed or withdrew the code meanwhile: Verwijderen disappears again. */
  onAdminCodeRefused: () => void;
  /** At least one order went: the list is read again. */
  onChanged: () => void;
  onClose: () => void;
}

/**
 * Several orders deleted in one go (Furkan, 08/10/2026: "bestellingen selecteren en een vuilbak knop die
 * meerdere kan verwijderen"): the same warning as for one order (in-app text (b) of the approved terms),
 * the list of what goes and what cannot, one reason for the orders in a closed Z. Each order is deleted
 * on its own, under the same rules as one by one; what the server refuses stays and is named.
 */
export function DeleteManyDialog({ orders, authorization, adminCode, onAdminCodeRefused, onChanged, onClose }: Props) {
  const { api, toast } = useTill();
  const text = t.orders.deleteMany;
  const [{ deletable, refused, afterZ }] = useState(() => sortForDelete(orders));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(0);
  const [error, setError] = useState<string | null>(null);
  /** What the server refused, with why; shown when the run is over. */
  const [failed, setFailed] = useState<{ code: string; message: string }[] | null>(null);

  const remove = async () => {
    if (afterZ.length > 0 && reason.trim().length < MIN_REASON) {
      setError(t.orders.deleteReasonMissing);
      return;
    }
    setBusy(true);
    setError(null);
    const refusals: { code: string; message: string }[] = [];
    let removed = 0;
    for (const order of deletable) {
      try {
        await deleteOne(api, order, { authorization, adminCode }, reason.trim());
        removed++;
        setDone(removed);
      } catch (failure) {
        const message = failure instanceof ApiError ? failure.message : t.errors.unexpected;
        // the special admin code no longer holds: nothing more can go, stop here
        if (failure instanceof ApiError && failure.code.includes("admin_code")) {
          setError(message);
          onAdminCodeRefused();
          break;
        }
        refusals.push({ code: order.code, message });
      }
    }
    if (removed > 0) {
      toast(text.deleted(removed));
      onChanged();
    }
    setBusy(false);
    if (refusals.length === 0 && removed === deletable.length) onClose();
    else setFailed(refusals);
  };

  const finished = failed !== null;
  return (
    <Dialog
      title={text.title(deletable.length)}
      onClose={onClose}
      footer={
        <>
          {busy && <span className="note">{text.progress(done, deletable.length)}</span>}
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose} disabled={busy} data-autofocus>
            {finished ? text.close : t.orders.back}
          </button>
          {!finished && (
            <button
              type="button"
              className="btn danger"
              disabled={busy || deletable.length === 0}
              onClick={() => void remove()}
            >
              {text.confirm(deletable.length)}
            </button>
          )}
        </>
      }
    >
      <div className="delbox">
        <p>
          {t.orders.deleteOnly[0]}
          <b>{t.orders.deleteOnly[1]}</b>
          {t.orders.deleteOnly[2]}
        </p>
        <p>
          {t.orders.deleteLimit[0]}
          <b>{t.orders.deleteLimit[1]}</b>
          {t.orders.deleteLimit[2]}
        </p>
        <p className="note">{t.orders.deleteResponsibility}</p>
      </div>
      {deletable.length > DAILY_LIMIT && (
        <p className="pinmsg" role="note">
          {text.overLimit(deletable.length)}
        </p>
      )}
      <ul className="delmany" aria-label={text.listLabel}>
        {deletable.map((order) => (
          <li key={order.id}>
            <b>{order.code}</b>
            <span>{formatEuro(order.totalCents)}</span>
            <span className="note">{order.zNumber === null ? "" : text.inZ(order.zNumber)}</span>
          </li>
        ))}
        {refused.map((order) => (
          <li key={order.id} className="stays">
            <b>{order.code}</b>
            <span>{formatEuro(order.totalCents)}</span>
            <span className="note">{text.stays}</span>
          </li>
        ))}
      </ul>
      {afterZ.length > 0 && !finished && (
        <div className="delbox after-z">
          <p>{text.afterZ(afterZ.length)}</p>
          <p>{t.orders.deleteAfterZKept}</p>
          <label>
            {t.orders.deleteReason}
            <input
              value={reason}
              maxLength={200}
              placeholder={t.orders.deleteReasonHint}
              onChange={(event) => {
                setReason(event.target.value);
                setError(null);
              }}
            />
          </label>
        </div>
      )}
      {error !== null && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      {finished && failed.length > 0 && (
        <div className="error-text" role="alert">
          <p>{text.someStayed(done, failed.length)}</p>
          <ul>
            {failed.map((item) => (
              <li key={item.code}>
                <b>{item.code}</b>: {item.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Dialog>
  );
}
